import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'

import {LABELS} from '../constants/labels'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import type {IssueViewerViewerFragment} from './IssueViewer'
import {DevelopmentSection, DevelopmentSectionFallback} from './sections/development-section/DevelopmentSection'
import {ParticipantsSection} from './sections/ParticipantsSection'
import {
  RelationshipsSectionFallback,
  RelationshipsSectionInternal,
} from './sections/relations-section/RelationshipsSection'
import {SubscriptionSection, SubscriptionSectionFallback} from './sections/subscription-section/SubscriptionSection'

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): this wrapper forwards the whole secondary issue
// node to its already-migrated child sections. The parent query is still Relay, so widen with RelayCompat
// to accept Relay data. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type IssueSidebarLazySectionsFragment = RelayCompat<IssueSecondaryNode>

type IssueSidebarLazySectionsBaseProps = {
  viewer: IssueViewerViewerFragment | null
  onLinkClick?: (event: MouseEvent) => void
  onIssueUpdate: () => void
  insideSidePanel?: boolean
  readonly: boolean
}

type IssueSidebarLazySectionsProps = IssueSidebarLazySectionsBaseProps & {
  issueData?: IssueSidebarLazySectionsFragment
}

export function IssueSidebarLazySections({
  issueData: data,
  onIssueUpdate,
  onLinkClick,
  insideSidePanel,
  viewer,
  readonly,
}: IssueSidebarLazySectionsProps) {
  if (!data) {
    return <IssueSidebarLazySectionsFallback />
  }

  // When the sticky header is enabled, the subscribe action is rendered there instead of in the sidebar.
  const showSubscriptionSection = !isFeatureEnabled('issues_sticky_header') && isLoggedIn()

  return (
    <>
      <RelationshipsSectionInternal
        issue={data}
        onLinkClick={onLinkClick}
        insideSidePanel={insideSidePanel}
        readonly={readonly}
        onSelection={onIssueUpdate}
      />
      <DevelopmentSection issue={data} onSelection={onIssueUpdate} readonly={readonly} />
      {showSubscriptionSection && <SubscriptionSection issue={data} viewer={viewer} />}
      <ParticipantsSection issue={data} />
    </>
  )
}

export function IssueSidebarLazySectionsFallback() {
  const showSubscriptionSection = !isFeatureEnabled('issues_sticky_header') && isLoggedIn()

  return (
    <>
      <RelationshipsSectionFallback />
      <DevelopmentSectionFallback />
      {showSubscriptionSection && <SubscriptionSectionFallback />}
    </>
  )
}

// Shown when a lazy sidebar child throws, containing the failure to this group instead of the whole page.
export function IssueSidebarLazySectionsErrorFallback() {
  const showSubscriptionSection = !isFeatureEnabled('issues_sticky_header') && isLoggedIn()
  const {relationships, development, notifications, participants, tryAgainLater} = LABELS.unavailableSections

  return (
    <>
      <Section sectionHeader={<ReadonlySectionHeader title={relationships} />} emptyText={tryAgainLater} />
      <Section sectionHeader={<ReadonlySectionHeader title={development} />} emptyText={tryAgainLater} />
      {showSubscriptionSection && (
        <Section sectionHeader={<ReadonlySectionHeader title={notifications} />} emptyText={tryAgainLater} />
      )}
      <Section sectionHeader={<ReadonlySectionHeader title={participants} />} emptyText={tryAgainLater} />
    </>
  )
}
